import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { MatchOffer, MatchState, MatchTurned } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { canFlip, cardAt, matchExplain, matchLead, missedPair } from '../../logic/minigames/match';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { animate, COLOURS, displayStyle } from '../board/ui';
import { columnsFor, CounterRow, gridCells } from './pieces';
import { TurnGameDialog } from './TurnGameDialog';
import type { MiniGameDeps } from './types';
import type { GameView } from '../../logic/minigames/core';

/** How long a missed pair stays face up. */
const MISS_SHOW_MS = 900;

interface CardView {
  root: Container;
  back: Sprite;
  face: Container;
  faceBase: Graphics;
  faceFrame: Sprite;
  picture: Sprite;
  value: Text;
  glow: Graphics;
  up: boolean;
}

/**
 * Find the pairs: twelve cards face down. Tap two to turn them; a pair
 * pays its value, and the tries run down either way. The slot's own
 * pictures are the faces, and a gold pair pays most. The Gold Pot is named
 * only when the server dealt this play the pot (`setup.goldPot`), and the
 * deck may have no gold pair at all: nothing here assumes one. At the end
 * every card turned shows; cards never turned stay face down.
 */
export class MatchDialog extends TurnGameDialog<MatchOffer, MatchState, MatchTurned> {
  private cards: CardView[] = [];
  private missed: number[] = [];
  private missTimer: number | null = null;
  private readonly counters: CounterRow;

  constructor(deps: MiniGameDeps) {
    super(deps, 'match', 'FIND THE PAIRS');
    this.counters = new CounterRow(['Tries left', 'Total'], (at) =>
      this.explain(matchExplain(this.offer?.setup?.tries ?? 6, this.offer?.setup?.goldPot ?? false), at),
    );
    this.content.addChild(this.counters);
  }

  protected begin(offer: MatchOffer): void {
    this.cards.forEach((c) => c.root.destroy({ children: true }));
    this.missed = [];
    const n = offer.setup?.cards ?? 12;
    this.cards = Array.from({ length: n }, (_, i) => {
      const root = new Container();
      const back = new Sprite();
      back.anchor.set(0.5);
      const face = new Container();
      const faceBase = new Graphics();
      const faceFrame = new Sprite();
      faceFrame.anchor.set(0.5);
      const picture = new Sprite();
      picture.anchor.set(0.5);
      const value = new Text({ text: '', style: displayStyle(14) });
      value.anchor.set(0.5);
      const glow = new Graphics();
      face.addChild(faceBase, picture, faceFrame, value);
      root.addChild(glow, back, face);
      root.eventMode = 'static';
      root.cursor = 'pointer';
      root.on('pointertap', () => this.onPiece(i));
      this.content.addChild(root);
      return { root, back, face, faceBase, faceFrame, picture, value, glow, up: false };
    });
  }

  protected onPiece(index: number): void {
    if (!canFlip(this.game, index)) return;
    // A new try turns the last miss back down straight away.
    this.clearMiss();
    this.act('flip', String(index), index);
  }

  protected override onAnswered(prev: GameView<MatchState, MatchTurned>, next: GameView<MatchState, MatchTurned>): void {
    const turned = next.last;
    if (!turned || !next.state) return;
    AudioBus.shared.play(GAME_SOUNDS.flip, { volume: 0.7 });
    const miss = missedPair(prev.state, next.state, turned);
    if (miss.length) {
      this.missed = miss;
      this.missTimer = window.setTimeout(() => {
        this.missed = [];
        this.missTimer = null;
        this.render();
      }, MISS_SHOW_MS);
    } else if (next.state.matched.includes(turned.index) && prev.state?.open !== null && prev.state?.open !== undefined) {
      window.setTimeout(() => AudioBus.shared.play(GAME_SOUNDS.pair, { volume: 0.8 }), 200);
    }
  }

  private clearMiss(): void {
    if (this.missTimer !== null) window.clearTimeout(this.missTimer);
    this.missTimer = null;
    if (this.missed.length) {
      this.missed = [];
      this.render();
    }
  }

  protected leadText(): string {
    return matchLead(this.game, this.offer?.setup?.goldPot ?? false);
  }

  protected pieceLabels(): string[] {
    return this.cards.map((_, i) => {
      const c = cardAt(this.game, i, this.missed);
      return c.up ? `Card ${i + 1}: ${c.face}${c.found ? ' (found)' : ''}` : `Card ${i + 1}`;
    });
  }

  protected renderPieces(y: number, width: number, maxHeight: number): number {
    const over = this.game.phase === 'over';
    const countersH = over ? 0 : this.counters.rowHeight + 8;
    const cols = columnsFor(this.cards.length);
    const { cells, size, h, height } = gridCells(this.cards.length, cols, width, 84, 6, 1.4, maxHeight - countersH);
    const back = this.deps.texture('road:match_back');
    const gold = this.deps.texture('road:match_gold');
    const frame = this.deps.texture('card:face');
    this.cards.forEach((c, i) => {
      const face = cardAt(this.game, i, this.missed);
      c.root.position.set(cells[i]!.x, y + cells[i]!.y);
      c.root.hitArea = { contains: (x: number, yy: number) => Math.abs(x) <= size / 2 && Math.abs(yy) <= h / 2 };
      c.root.cursor = canFlip(this.game, i) ? 'pointer' : 'default';
      // The back: the painted card, or a drawn one while it loads.
      if (back) {
        c.back.texture = back;
        c.back.width = size;
        c.back.height = h;
      }
      c.back.visible = !face.up;
      c.face.visible = face.up;
      c.glow.clear();
      if (face.up) {
        const isGold = face.face === 'gold';
        c.faceBase.clear().roundRect(-size / 2 + 2, -h / 2 + 2, size - 4, h - 4, 8).fill({ color: isGold ? 0xf3c64a : 0xf6ead0 });
        c.faceFrame.visible = !isGold && !!frame;
        if (frame && !isGold) {
          c.faceFrame.texture = frame;
          // The frame art is a square with the card at x 194-829, y 61-938 of 1024.
          c.faceFrame.width = (size * 1024) / 635;
          c.faceFrame.height = (h * 1024) / 877;
        }
        const pic = isGold ? gold : this.deps.picture(Number(face.face.slice(1)));
        if (pic) {
          c.picture.texture = pic;
          const k = isGold ? Math.min(size / pic.width, h / pic.height) : Math.min((size * 0.78) / pic.width, (h * 0.6) / pic.height);
          c.picture.scale.set(k);
          c.picture.y = isGold ? 0 : -h * 0.08;
        }
        c.value.text = face.coins.toLocaleString('en-GB');
        c.value.style = displayStyle(Math.max(12, Math.round(size * 0.2)));
        c.value.position.set(0, h * 0.32);
        if (face.found) {
          c.glow.roundRect(-size / 2 - 3, -h / 2 - 3, size + 6, h + 6, 10).stroke({ color: COLOURS.gold, width: 3, alpha: 0.9 });
        }
      } else if (!back) {
        c.glow.roundRect(-size / 2, -h / 2, size, h, 8).fill({ color: 0x2f5a2a }).stroke({ color: COLOURS.brass, width: 2 });
      }
      if (face.up !== c.up) {
        c.up = face.up;
        void animate(220, (t) => (c.root.scale.x = Math.max(0.05, Math.abs(Math.cos((1 - t) * Math.PI * 0.5)))));
      }
      // At the end, pairs found stand out; the rest fade.
      c.root.alpha = over && !(face.up && face.found) ? (face.up ? 0.7 : 0.45) : 1;
    });
    const s = this.game.state;
    this.counters.visible = !over;
    if (over) return y + height;
    this.counters.set([String(s?.triesLeft ?? this.offer?.setup?.tries ?? 6), (s?.total ?? 0).toLocaleString('en-GB')], width);
    this.counters.position.set(0, y + height + 8);
    return y + height + countersH;
  }
}
